import { Alert, Button, Group, Loader, Stack, Text, Textarea, Title } from "@mantine/core";
import { ArrowRight, Download, LockKeyhole, Sparkles } from "lucide-react";
import { useState, type ReactNode } from "react";

import type { FirstAdState } from "../../api/generated/model";
import { FirstAdApprove, firstAdApproveLinesItemMax } from "../../api/generated/zod";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { Logo } from "../../ui/Logo";
import classes from "./creation-flow.module.css";

export type CreationPhase = "product" | "actor" | "script" | "keyframes" | "video";
export type PlanReason = "remove-watermark" | "another-ad";
export interface FirstAdCreationProps {
  state: FirstAdState;
  /** Display navigation only; the server still owns eligibility and saved progress. */
  phase: CreationPhase;
  styleName: string;
  productStep: ReactNode;
  actorStep: ReactNode;
  tools: readonly { id: string; label: string }[];
  /** Taken from authoritative completion state, never inferred from the visible phase. */
  toolsUnlocked: boolean;
  pending?: boolean;
  error?: unknown;
  videoUrl?: string | null;
  posterUrl?: string | null;
  onApprove: (lines: string[]) => void;
  onGetVideo: () => void;
  onDownload: () => void;
  onRefreshMedia: () => void;
  onPlans: (reason: PlanReason) => void;
  onOpenTool: (id: string) => void;
  onContinueToApp?: () => void;
  onRetry?: () => void;
}
const STEPS: readonly [CreationPhase, string][] = [
  ["product", "Product"], ["actor", "Actor"], ["script", "Script"],
  ["keyframes", "Preview"], ["video", "Video"],
];
const HEADINGS: Record<CreationPhase, string> = {
  product: "We found your product", actor: "Choose your AI actor", script: "Your script",
  keyframes: "A first look at your ad", video: "Making your ad",
};

/** One mock-driven view for all catalog templates. It performs no fetch or generation. */
export function FirstAdCreation(props: FirstAdCreationProps) {
  const { state, phase, pending = false, videoUrl, posterUrl } = props;
  const hasVideo = phase === "video" && !!videoUrl && !state.working && !state.problem && !props.error;
  const previews = state.previews.slice(0, 3);
  const canGetVideo = (state.video_ready || state.run_job_id !== null)
    && previews.length === 3 && previews.every((frame) => !!frame.url)
    && !pending && (!state.working || state.run_job_id !== null) && !state.problem && !props.error;
  return <div className={classes.frame}>
    <aside className={classes.rail} aria-label="App tools">
      <div className={classes.logo}><Logo /></div>
      <Text size="xs" id="first-ad-tools-note" c="dimmed">
        {props.toolsUnlocked ? "Your tools are ready" : "Unlock tools by finishing your first ad"}
      </Text>
      <nav className={classes.tools}>
        {props.tools.map((tool) => <button type="button" key={tool.id}
          title={props.toolsUnlocked ? undefined : `Unlock ${tool.label} by finishing your first ad`}
          disabled={!props.toolsUnlocked} onClick={() => props.onOpenTool(tool.id)}
          aria-describedby="first-ad-tools-note">
          {props.toolsUnlocked ? <ArrowRight size={16} aria-hidden /> : <LockKeyhole size={16} aria-hidden />}
          <span>{tool.label}</span>
        </button>)}
      </nav>
      {props.onContinueToApp ? <Button variant="subtle" onClick={props.onContinueToApp}>Continue to app</Button> : null}
    </aside>
    <div className={classes.workspace}>
      <header className={classes.top}>
        <Text size="sm" fw={600}>{props.styleName}</Text>
        <nav aria-label="First ad steps"><ol className={classes.steps}>
          {STEPS.map(([id, label]) => <li key={id} aria-current={phase === id ? "step" : undefined}>{label}</li>)}
        </ol></nav>
      </header>
      <main id="main" className={classes.main}>
        <div className={classes.heading}>
          <Title order={1}>{hasVideo ? "Your ad is ready" : HEADINGS[phase]}</Title>
          {phase === "script" ? <Text c="dimmed">Edit it until it sounds like you.</Text> : null}
          {phase === "actor" ? <Text c="dimmed">Pick the person who will tell your product's story.</Text> : null}
          {phase === "keyframes" ? <Text c="dimmed">Three keyframes from your ad.</Text> : null}
          {hasVideo ? <Text c="dimmed">Your first ad is free to watch and download with its watermark.</Text> : null}
        </div>
        {props.error ? <ErrorAlert error={props.error} /> : null}
        {state.problem ? <Alert color="red" role="alert" title="Your ad needs attention">{state.problem}</Alert> : null}
        {state.offer?.retry_available && props.onRetry ? <Button variant="light" disabled={pending}
          onClick={props.onRetry}>Retry your free ad</Button> : null}
        {phase === "product" ? props.productStep : null}
        {phase === "actor" ? props.actorStep : null}
        {phase === "script" ? (state.lines.length > 0
          ? <ScriptEditor key={JSON.stringify([state.template_id, state.lines])} lines={state.lines} cap={state.script_max_chars}
            busy={pending || state.working} onApprove={props.onApprove} />
          : !state.problem && !props.error ? <Working>Writing your script</Working> : null) : null}
        {phase === "keyframes" ? <>
          <div className={classes.previews} aria-label="Ad keyframes">
            {[0, 1, 2].map((index) => {
              const url = previews[index]?.url;
              return <div key={index} className={classes.preview}>
                {url ? <DeliveredMedia key={url} src={url} kind="image" label={`Keyframe ${index + 1} from your ad`} onExpired={props.onRefreshMedia} />
                  : state.problem || props.error ? <Text size="sm">Preview unavailable</Text>
                    : <output><Loader size="sm" /><Text size="sm">Making preview {index + 1}</Text></output>}
              </div>;
            })}
          </div>
          {state.preview_mode === "video" && !previews.some((frame) => frame.url)
            ? <Text c="dimmed" ta="center">Your preview will appear as your video is made.</Text> : null}
          <div className={classes.actions}><Button size="lg" onClick={props.onGetVideo} disabled={!canGetVideo}>
            Get video <ArrowRight size={18} aria-hidden />
          </Button></div>
        </> : null}
        {phase === "video" ? (hasVideo ? <>
          <div className={classes.video}><DeliveredMedia key={videoUrl} src={videoUrl ?? ""} kind="video"
            label="Your first ad, watermarked" poster={posterUrl} onExpired={props.onRefreshMedia} /></div>
          <Group justify="center" className={classes.actions}>
            <Button variant="default" leftSection={<Download size={16} />} onClick={props.onDownload} disabled={pending}>Download with watermark</Button>
            <Button variant="light" leftSection={<Sparkles size={16} />} onClick={() => props.onPlans("remove-watermark")} disabled={pending}>Remove watermark</Button>
            <Button onClick={() => props.onPlans("another-ad")} disabled={pending}>Make another ad</Button>
          </Group>
        </> : !state.problem && !props.error ? <Working>Making your ad</Working> : null) : null}
      </main>
    </div>
  </div>;
}

function Working({ children }: { children: ReactNode }) {
  return <output className={classes.working}><Loader /><Text>{children}</Text>
    <Text size="sm" c="dimmed">You can leave this page and come back.</Text></output>;
}

function ScriptEditor({ lines, cap, busy, onApprove }: {
  lines: string[]; cap: number | null; busy: boolean; onApprove: (lines: string[]) => void;
}) {
  const [edited, setEdited] = useState(lines);
  const length = edited.reduce((sum, line) => sum + line.length, 0);
  const tooLong = cap !== null && length > cap;
  const valid = FirstAdApprove.safeParse({ lines: edited }).success && edited.every((line) => line.trim());
  return <form onSubmit={(event) => { event.preventDefault(); if (!busy && !tooLong && valid) onApprove([...edited]); }}>
    <Stack className={classes.script}>
      {edited.map((line, index) => <Textarea key={index} label={`Line ${index + 1}`} value={line}
        disabled={busy} autosize minRows={2} maxRows={6} maxLength={firstAdApproveLinesItemMax}
        onChange={(event) => { const text = event.currentTarget.value; setEdited((current) => current.map((value, at) => at === index ? text : value)); }} />)}
      {cap !== null ? <Text component="output" size="sm" c={tooLong ? "red" : "dimmed"}>{length} / {cap} characters</Text> : null}
    </Stack>
    <div className={classes.actions}><Button type="submit" size="lg" loading={busy}
      disabled={busy || tooLong || !valid}>Continue <ArrowRight size={18} aria-hidden /></Button></div>
  </form>;
}


/** Keep the element mounted so a refreshed URL or media cookie can recover it. */
function DeliveredMedia({ src, kind, label, poster, onExpired }: {
  src: string; kind: "image" | "video"; label: string; poster?: string | null | undefined; onExpired: () => void;
}) {
  const [failed, setFailed] = useState(false);
  const onError = () => { if (!failed) { setFailed(true); onExpired(); } };
  const recovered = () => setFailed(false);
  return <div className={classes.delivered}>
    {kind === "image"
      ? <img src={src} alt={label} aria-hidden={failed || undefined} style={{ visibility: failed ? "hidden" : undefined }} onError={onError} onLoad={recovered} />
      // A generated video has no caption track, and plays its own sound (spec web/player-sound).
      // oxlint-disable-next-line jsx-a11y/media-has-caption
      : <video src={src} {...(poster ? { poster } : {})} controls playsInline preload={poster ? "none" : "metadata"}
        aria-label={label} aria-hidden={failed || undefined} style={{ visibility: failed ? "hidden" : undefined }} onError={onError} onLoadedData={recovered} />}
    {failed ? <Text className={classes.mediaProblem} size="sm" component="output">The preview did not load.</Text> : null}
  </div>;
}
